import React from 'react';

import styled from 'styled-components';

import { getMoment } from '@libs/NwMoment';

import { getContactName } from '@hooks/API/Contact/UseContactsList';

import Money from '@components/Gui/Money';
import NwIcon from '@components/Gui/NwIcon';
import StyleVariables from '@components/Gui/StyleVariables.js';

import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

/**
 * InvoiceItem
 *
 * Universal component for displaying a single invoice item.
 * Reusable across all contact types.
 */

// Default cursor when the item has no click handler, otherwise it depends on accounting access
const getCursor = (props) => {
    if (!props.$clickable) {
        return 'default';
    }
    return props.$pointercursor ? 'pointer' : 'not-allowed';
};

const Wrapper = styled.div`
    background: #fff;
    display: flex;
    flex-direction: row;
    cursor: ${getCursor};
    user-select: none;
    border-bottom: ${(props) => props.theme.DefaultBorder};

    .invoice-number {
        width: 14rem;
        font-weight: bold;
        font-size: 1.1rem;
        text-align: center;
        padding-top: 0.75rem;
    }

    .invoice-detail {
        padding: 0.25rem 0.5rem;
        flex: 1 1 0;
        border-right: ${StyleVariables.DarkerBorder};

        .invoice-date {
            font-size: ${(props) => props.theme.FontSizeSm};
        }

        small {
            font-style: italic;
            opacity: 0.75;
            display: block;
        }
    }
    .invoice-totals {
        width: 10rem;
        padding: 0.25rem 0.5rem;
        text-align: right;

        span.title-span {
            text-transform: uppercase;
            font-size: ${(props) => props.theme.FontSizeSm};
            display: block;
        }
        span.money-span {
            font-weight: bold;
            display: block;
        }
    }
    .invoice-actions {
        display: flex;
        align-items: center;
        padding: 0.25rem 0.5rem;
    }
`;

const InvoiceItem = ({ invoice, onInvoiceClick, hasAccountingAccess, actions, hideNumber = false }) => {
    const handleInvoiceClick = () => {
        if (onInvoiceClick) {
            onInvoiceClick(invoice.ID);
        }
    };

    return (
        <Wrapper onClick={handleInvoiceClick} $clickable={!!onInvoiceClick} $pointercursor={hasAccountingAccess}>
            {!hideNumber && (
                <div className="invoice-number">
                    {invoice.Number ? invoice.Number : <NwIcon icon={byPrefixAndName.fasl['empty-set']} />}
                </div>
            )}
            <div className="invoice-detail">
                <div className="contact-info">
                    {getContactName(invoice.ContactID).toUpperCase()} / {invoice.BillingInfo.CompanyName}
                </div>
                <small>{invoice.Name || 'no description'}</small>
                <div className="invoice-date">
                    {invoice.Date ? getMoment(invoice.Date).format('DD MMM YYYY') : 'N.D'}
                </div>
            </div>
            <div className="invoice-totals">
                <span className="title-span">Total Amount</span>
                <Money amount={invoice.TotalAmount} currency={invoice.Currency} />
            </div>
            {actions && <div className="invoice-actions">{actions}</div>}
        </Wrapper>
    );
};

export default InvoiceItem;

